import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, oauthStartUrl } from '../api';
import { Notice, StatusBadge, relativeTime } from '../components';
import type {
  ClientDetail as Detail,
  ConnectionHealth,
  FeatureConfig,
  LookerCredentials,
} from '../types';
import { AccountPicker } from '../AccountPicker';
import { WindsorPicker } from '../WindsorPicker';
import { ReportLinks } from '../ReportLinks';

export function ClientDetail({ onSignedOut }: { onSignedOut: () => void }) {
  const { slug = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [health, setHealth] = useState<ConnectionHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const [features, setFeatures] = useState<FeatureConfig | null>(null);
  const [pickingWindsor, setPickingWindsor] = useState<'linkedin' | 'instagram' | null>(null);

  // Shown once, immediately after creation or rotation. Seeded from the query
  // string that /clients redirects to, then cleared from the URL so it doesn't
  // linger in history.
  const [password, setPassword] = useState<string | null>(searchParams.get('password'));

  // Set by the OAuth callback: accounts discovered, waiting to be chosen.
  const [pendingId, setPendingId] = useState<string | null>(searchParams.get('pending'));

  const connected = searchParams.get('connected');
  const oauthError = searchParams.get('error');

  const load = useCallback(async () => {
    try {
      const [clientDetail, healthRows, featureConfig] = await Promise.all([
        api.getClient(slug),
        api.health(),
        api.config(),
      ]);
      setDetail(clientDetail);
      setHealth(healthRows);
      setFeatures(featureConfig);
      setError('');
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) {
        onSignedOut();
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [slug, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  // Strip one-shot query params once they've been read into state.
  useEffect(() => {
    if (
      searchParams.has('password') ||
      searchParams.has('pending') ||
      searchParams.has('connected') ||
      searchParams.has('error')
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete('password');
      next.delete('pending');
      setSearchParams(next, { replace: true });
    }
    // Intentionally runs once per navigation, not on every searchParams tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function rotate() {
    if (
      !confirm(
        'Rotate this client’s database password?\n\n' +
          'Any Looker Studio data source using the old password will stop refreshing ' +
          'until you update it.',
      )
    ) {
      return;
    }
    setBusy('rotate');
    try {
      const creds = await api.rotateCredentials(slug);
      setPassword(creds.password);
      setMessage('Rotated password.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rotation failed');
    } finally {
      setBusy('');
    }
  }

  async function disconnect(connectionId: number, name: string, purge: boolean) {
    const question = purge
      ? `Disconnect ${name} AND permanently delete all of its report data?\n\n` +
        'Use this for a data-deletion request. It cannot be undone, and past ' +
        'reports for this account will be empty.'
      : `Disconnect ${name}? Syncing stops; existing report data is kept.`;
    if (!confirm(question)) return;
    setBusy(`disconnect-${connectionId}`);
    try {
      const result = await api.deleteConnection(connectionId, purge);
      if (purge) setMessage(`Disconnected ${name} and deleted ${result.rows_deleted} rows.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setBusy('');
    }
  }

  async function sync(days?: number) {
    setBusy(days ? 'backfill' : 'sync');
    setMessage('');
    try {
      await api.triggerSync(slug, days);
      setMessage('Sync queued. Refresh in a minute to see results.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start sync');
    } finally {
      setBusy('');
    }
  }

  if (loading) return <div className="spinner">Loading…</div>;
  if (!detail) return <Notice kind="err">{error || `No client "${slug}"`}</Notice>;

  const healthById = new Map(health.map((h) => [h.connection_id, h]));
  const looker: LookerCredentials = detail.looker;

  return (
    <>
      <header className="top">
        <div>
          <h1>{detail.client.name}</h1>
          <div className="sub mono">
            client_{detail.client.slug} &middot; {detail.client.timezone}
          </div>
        </div>
        <Link className="btn" to="/">
          &larr; All clients
        </Link>
      </header>

      {connected && <Notice kind="ok">Connected {connected}. The next sync will pull its history.</Notice>}
      {oauthError && <Notice kind="err">{oauthError}</Notice>}
      {error && <Notice kind="err">{error}</Notice>}
      {message && <Notice kind="ok">{message}</Notice>}

      {pendingId && (
        <AccountPicker
          pendingId={pendingId}
          slug={slug}
          onDone={(done) => {
            setPendingId(null);
            if (done) setMessage(done);
            void load();
          }}
        />
      )}

      <div className="card">
        <h2>Connected accounts</h2>

        {detail.connections.length === 0 ? (
          <p className="muted">
            Nothing connected yet. Use the buttons below to authorise this client&rsquo;s
            Instagram and LinkedIn accounts.
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Account</th>
                  <th>Status</th>
                  <th>Last sync</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {detail.connections.map((connection) => {
                  const row = healthById.get(connection.id);
                  return (
                    <tr key={connection.id}>
                      <td>
                        <span style={{ textTransform: 'capitalize' }}>{connection.platform}</span>
                        {connection.provider === 'windsor' && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            via Windsor.ai
                          </div>
                        )}
                      </td>
                      <td>
                        {connection.external_name ?? '—'}
                        <span className="muted mono truncate">{connection.external_id}</span>
                      </td>
                      <td>
                        <StatusBadge
                          status={connection.status}
                          lastRunStatus={row?.last_run_status}
                        />
                        {connection.status_detail && (
                          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                            {connection.status_detail}
                          </div>
                        )}
                      </td>
                      <td className="muted">
                        {relativeTime(row?.last_run_at ?? null)}
                        {row?.rows_written ? (
                          <div style={{ fontSize: 12 }}>{row.rows_written} rows</div>
                        ) : null}
                      </td>
                      <td>
                        <div className="row" style={{ gap: 6 }}>
                          <button
                            className="shrink"
                            disabled={busy === `disconnect-${connection.id}`}
                            onClick={() =>
                              disconnect(
                                connection.id,
                                connection.external_name ?? connection.external_id,
                                false,
                              )
                            }
                          >
                            Disconnect
                          </button>
                          <button
                            className="danger shrink"
                            disabled={busy === `disconnect-${connection.id}`}
                            onClick={() =>
                              disconnect(
                                connection.id,
                                connection.external_name ?? connection.external_id,
                                true,
                              )
                            }
                          >
                            Delete data
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="row" style={{ marginTop: 18 }}>
          {features?.instagram !== false && (
            <a className="btn primary shrink" href={oauthStartUrl('meta', detail.client.id)}>
              Connect Instagram (Meta)
            </a>
          )}
          {/* Windsor covers Instagram while Meta business verification is
              pending; the same account can move to Meta later, history kept. */}
          {features?.instagram_windsor && (
            <button className="shrink" onClick={() => setPickingWindsor('instagram')}>
              Link Instagram (Windsor)
            </button>
          )}
          {features?.linkedin_windsor && (
            <button className="primary shrink" onClick={() => setPickingWindsor('linkedin')}>
              Link LinkedIn (Windsor)
            </button>
          )}
          {/* Our own LinkedIn app only works once LinkedIn approves it; until
              then the button would just produce a 403, so it stays hidden. */}
          {features?.linkedin_native && (
            <a className="btn shrink" href={oauthStartUrl('linkedin', detail.client.id)}>
              Connect LinkedIn (direct)
            </a>
          )}
          <button className="shrink" disabled={busy === 'sync'} onClick={() => sync()}>
            {busy === 'sync' ? 'Starting…' : 'Sync now'}
          </button>
          <button className="shrink" disabled={busy === 'backfill'} onClick={() => sync(730)}>
            {busy === 'backfill' ? 'Starting…' : 'Backfill 2 years'}
          </button>
        </div>

        {pickingWindsor && (
          <WindsorPicker
            key={pickingWindsor}
            slug={slug}
            platform={pickingWindsor}
            onCancel={() => setPickingWindsor(null)}
            onLinked={(name) => {
              setPickingWindsor(null);
              setMessage(`Linked ${name}. Run a backfill to pull its history.`);
              void load();
            }}
          />
        )}
      </div>

      <ReportLinks client={detail.client} />

      <div className="card">
        <h2>Looker Studio connection</h2>
        <p className="muted" style={{ marginTop: -6 }}>
          In Looker Studio choose <strong>Create &rarr; Data source &rarr; PostgreSQL</strong>,
          enter these details, then add one data source per view below.
        </p>

        <div className="creds">
          {`Host        ${looker.host}
Port        ${looker.port}
Database    ${looker.database}
Username    ${looker.username}
Password    ${password ?? '(shown once — rotate to issue a new one)'}
Enable SSL  yes`}
        </div>

        {password ? (
          <Notice kind="warn">
            This password is shown once and is not stored. Copy it now.
          </Notice>
        ) : (
          <p className="muted" style={{ marginTop: 12 }}>
            Passwords are never stored. Rotate only if the password is lost or leaked
            &mdash; the client&rsquo;s Looker data sources will need the new one.
          </p>
        )}

        <h3>Tables to add</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>View</th>
                <th>Used for</th>
                <th>Date field</th>
              </tr>
            </thead>
            <tbody>
              {looker.views.map((view) => (
                <tr key={view.name}>
                  <td className="mono">{view.name}</td>
                  <td className="muted">{view.purpose}</td>
                  <td className="mono">{view.date_field}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 16 }}>
          <button disabled={busy === 'rotate'} onClick={rotate}>
            {busy === 'rotate' ? 'Rotating…' : 'Rotate password'}
          </button>
        </div>
      </div>
    </>
  );
}
